import React, { useState, useEffect } from "react";
import {
  CFormGroup,
  CInput,
  CLabel,
  CButton,
  CAlert,
  CInputRadio,
  CCard,
  CCardHeader,
  CCardBody,
  CCardFooter,
} from "@coreui/react";
import { getPaymentMethods } from "./../../features/PaymentMethods/PaymentMethods.slice";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import AddBankAccountModal from "./../../features/PaymentMethods/AddBankAccountModal";
import AddCreditCardModal from "./../../features/PaymentMethods/AddCreditCardModal";
import { refreshAccountSummary } from "../../AccountSummary.slice";
import { pushToast } from "../../features/Toaster/Toaster.slice";
import { sendPaymentApi } from "../../apis/apis";
import { formatCurrency } from "../../utils/currency";
import { AutopayInfoAlert } from "../../features/Dashboard/components/AutopayInfoAlert";
import MakePaymentModal from "./MakePaymentModal";
import { formatDate } from "./../../utils/string";
import { trackEvent } from "./../../utils/analytics";
import SecureDataTransfer from "./../../components/SecureDataTransfer/SecureDataTransfer";
import { Status } from "../../features/PaymentsHistory/utils";
import { useNavigate, Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { PaymentMethodSourceLabel } from "../../features/PaymentMethods/components/PaymentMethodSourceLabel";
import { formatPaymentMethodLabel } from "../../features/PaymentMethods/utils";
import { isOwnerEnrolledInAutopay } from "../../features/Dashboard/components/setAutopayPaymentMethod";

export const MakePayment: React.FC = () => {
  const [bankAccountModal, setBankAccountModal] = useState(false);
  const [creditCardModal, setCreditCardModal] = useState(false);
  const [paymentConfirmModal, setPaymentConfirmModal] = useState<any>(false);
  const navigate = useNavigate();
  const accountSummary = useAppSelector((state) => state.accountSummary);
  const paymentMethods = useAppSelector((state) => state.payment.methods);
  const { loading: loadingPM } = paymentMethods;
  const { loading: loadingAM, data, activeTenantAccountIndex } = accountSummary;
  const tenantAccountData = data?.[activeTenantAccountIndex];
  const tenantAccountId = tenantAccountData?.id;
  const lastStatement = tenantAccountData?.invoice;
  const lastSuccessfulPayment = tenantAccountData?.lastSuccessfulPayment;
  const isAutopayEnabled = tenantAccountData?.autopay?.enabled;
  const isOwnerAutopayEnrolled = tenantAccountData
    ? isOwnerEnrolledInAutopay(tenantAccountData)
    : false;
  const autopayPaymentMethodId = isOwnerAutopayEnrolled
    ? tenantAccountData?.autopay?.paymentMethodId
    : null;

  // Calculate the different balance types
  const invoiceBalance = lastStatement?.remainingBalance || 0; // What's on the invoice (safest, what customer sees)
  const totalBalance = tenantAccountData?.billedBalanceTenantToCorp || invoiceBalance; // Total outstanding (for max payment)
  
  // Show "no payment" message only if total balance is zero or negative
  const hasBalance = totalBalance > 0;

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getPaymentMethods());
    trackEvent({ action: "View > Make Payment" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleBankAccountModal = (
    visible = !bankAccountModal,
    setFieldValue: any
  ) => {
    if (typeof setFieldValue === "function") {
      setFieldValue("paymentMethodId", paymentMethods?.data[0]?.id);
    }
    setBankAccountModal(visible);
  };

  const toggleCreditCardModal = (
    visible = !creditCardModal,
    setFieldValue: any
  ) => {
    if (typeof setFieldValue === "function") {
      setFieldValue("paymentMethodId", paymentMethods?.data[0]?.id);
    }
    setCreditCardModal(visible);
  };

  if (loadingAM || loadingPM) {
    return <div>Loading...</div>;
  }

  // Show "no payment needed" when total balance is zero or negative
  if (!hasBalance) {
    return (
      <div className="flex-grow-1 justify-content-center">
        <CCard>
          <CCardHeader className="h4">Make Payment</CCardHeader>
          <CCardBody>
            No energy statements require payment at this time. Thank you.
          </CCardBody>
        </CCard>
      </div>
    );
  }

  // Edge case: If there's a balance but no invoice, show error message
  // This should be extremely rare - only happens for brand new accounts with charges but no invoice yet
  if (!lastStatement) {
    return (
      <div className="flex-grow-1 justify-content-center">
        <CCard>
          <CCardHeader className="h4">Make Payment</CCardHeader>
          <CCardBody>
            <CAlert color="warning">
              <p>
                Your account has an outstanding balance of{" "}
                <strong>{formatCurrency(totalBalance)}</strong>, but no
                invoice is available yet.
              </p>
              <p>
                Please contact support@kingenergy.com or check back shortly.
                Your invoice may be in the process of being generated.
              </p>
            </CAlert>
          </CCardBody>
        </CCard>
      </div>
    );
  }

  const paymentMethodRadios = paymentMethods?.data?.map((pm, key: number) => (
    <div className="form-check" key={`pm-${key}`}>
      <Field
        as={CInputRadio}
        type="radio"
        name="paymentMethodId"
        value={pm.id}
        id={`pm-${key}`}
      />
      <CLabel htmlFor={`pm-${key}`}>
        <PaymentMethodSourceLabel
          paymentMethod={pm}
          showAutopayBadge={
            Boolean(autopayPaymentMethodId) && pm.id === autopayPaymentMethodId
          }
        />
      </CLabel>
    </div>
  ));

  return (
    <div className="flex-grow-1 justify-content-center">
      <CCard>
        <Formik
          initialValues={{
            invoiceBalance: invoiceBalance,
            totalBalanceAmount: totalBalance,
            paymentAmount: invoiceBalance > 0 ? "INVOICE_BALANCE" : "TOTAL_BALANCE",
            paymentAmountOther: "",
            paymentMethodId:
              paymentMethods?.defaultPaymentMethod?.id ??
              paymentMethods?.data[0]?.id ??
              "",
          }}
          enableReinitialize
          validationSchema={Yup.object().shape({
            invoiceBalance: Yup.string(),
            totalBalanceAmount: Yup.string(),
            paymentAmount: Yup.string().required(
              "Please choose a payment amount"
            ),
            paymentAmountOther: Yup.string().when("paymentAmount", {
              is: (val: string) => val === "INVOICE_BALANCE" || val === "TOTAL_BALANCE",
              otherwise: (s) =>
                s
                  .required("Please enter a payment amount")
                  .test("num", "Must be a number", function (value: any) {
                    return !isNaN(Number(value));
                  })
                  .test(
                    "min",
                    "Must be greater than zero",
                    function (value: any) {
                      return Number(value) > 0;
                    }
                  )
                  .matches(
                    /^[0-9]+(.[0-9]{2})?$/,
                    "Please enter whole dollars and cents (e.g. 100.00)"
                  )
                  .test(
                    "max",
                    `Cannot exceed total balance of ${formatCurrency(totalBalance)}`,
                    function (value: any) {
                      return Number(value) <= totalBalance;
                    }
                  ),
            }),
            paymentMethodId: Yup.string().required(
              `Please ${
                paymentMethods?.data?.length === 0 ? "add" : "choose"
              } a payment account`
            ),
          })}
          onSubmit={async (values, { setSubmitting }) => {
            const amount =
              values.paymentAmount === "INVOICE_BALANCE"
                ? invoiceBalance
                : values.paymentAmount === "TOTAL_BALANCE"
                ? totalBalance
                : values.paymentAmountOther;
            const selectedPaymentMethod = paymentMethods?.data.find(
              (pm: any) => pm.id === values.paymentMethodId
            );

            if (!selectedPaymentMethod) {
              setSubmitting(false);
              return;
            }

            const paymentMethodLabel = formatPaymentMethodLabel(
              selectedPaymentMethod
            );

            try {
              await new Promise((resolve, reject) => {
                setPaymentConfirmModal(
                  <MakePaymentModal
                    amount={Number(amount)}
                    paymentMethod={selectedPaymentMethod}
                    resolve={resolve}
                    reject={reject}
                  />
                );
              });
            } catch (e: any) {
              setPaymentConfirmModal(false);
              setSubmitting(false);
              return;
            }

            try {
              const paymentTransaction = await sendPaymentApi(
                tenantAccountId,
                lastStatement.id,
                {
                  amount: Number(amount),
                  paymentDate: Date.now(),
                  paymentMethodId: values.paymentMethodId,
                }
              );

              if (paymentTransaction.status === Status.FAILED) {
                dispatch(
                  pushToast({
                    message: `Your payment of ${formatCurrency(
                      Number(amount)
                    )} from ${paymentMethodLabel} failed.`,
                    type: "error",
                  })
                ).catch((e: string) => {
                  throw new Error(e);
                });
              } else {
                dispatch(
                  pushToast({
                    message: `Your payment of ${formatCurrency(
                      Number(amount)
                    )} from ${paymentMethodLabel} was sent successfully. Thank you!`,
                    type: "success",
                  })
                ).catch((e: string) => {
                  throw new Error(e);
                });
                navigate("/thank-you", {
                  state: {
                    lastStatement,
                    paymentTransaction,
                  },
                });
              }

              dispatch(refreshAccountSummary()).catch((e: string) => {
                throw new Error(e);
              });
            } catch (e) {
              console.error(e);
              if (e instanceof Error) {
                dispatch(
                  pushToast({
                    message: `${e?.message ? `${e?.message}:` : "Error:"} ${
                      e.message
                    }`,
                    type: "error",
                  })
                ).catch((e: string) => {
                  throw new Error(e);
                });
              }
            } finally {
              setPaymentConfirmModal(false);
            }
            setSubmitting(false);
          }}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            isValidating,
            isValid,
            submitCount,
            setFieldValue,
          }) => (
            <>
              <Form>
                <CCardHeader className="h2">Make Payment</CCardHeader>
                <CCardBody>
                  <CFormGroup
                    className={
                      errors.paymentAmountOther && touched.paymentAmountOther
                        ? "error"
                        : ""
                    }
                  >
                    <CLabel>Payment amount</CLabel>
                    {/* Show invoice balance option only if different from total */}
                    {invoiceBalance > 0 && invoiceBalance < totalBalance && (
                      <div className="form-check">
                        <Field
                          as={CInputRadio}
                          type="radio"
                          name="paymentAmount"
                          value="INVOICE_BALANCE"
                          id="payment-invoice-balance"
                        />
                        <CLabel htmlFor="payment-invoice-balance">
                          <strong>Balance due:</strong>{" "}
                          {formatCurrency(invoiceBalance)}
                        </CLabel>
                      </div>
                    )}
                    {/* Show total balance option */}
                    {totalBalance > 0 && (
                      <div className="form-check">
                        <Field
                          as={CInputRadio}
                          type="radio"
                          name="paymentAmount"
                          value="TOTAL_BALANCE"
                          id="payment-total-balance"
                        />
                        <CLabel htmlFor="payment-total-balance">
                          <strong>{invoiceBalance === totalBalance ? "Balance due:" : "Total account balance:"}</strong>{" "}
                          {formatCurrency(totalBalance)}
                          {invoiceBalance > 0 && invoiceBalance < totalBalance && (
                            <span style={{ fontSize: "0.9em", color: "#666", marginLeft: "8px" }}>
                              (pay off account in full)
                            </span>
                          )}
                          {invoiceBalance === 0 && totalBalance > 0 && (
                            <span style={{ fontSize: "0.9em", color: "#666", marginLeft: "8px", display: "block" }}>
                              (No payment due at this time)
                            </span>
                          )}
                        </CLabel>
                      </div>
                    )}
                    {/* Other amount option */}
                    <div className="form-check">
                      <Field
                        as={CInputRadio}
                        type="radio"
                        name="paymentAmount"
                        value="OTHER"
                        id="payment-other"
                      />
                      <CLabel htmlFor="payment-other">Other amount (up to {formatCurrency(totalBalance)})</CLabel>
                    </div>
                    {values.paymentAmount === "OTHER" && (
                      <CFormGroup
                        className={
                          errors.paymentAmountOther &&
                          touched.paymentAmountOther
                            ? "error"
                            : ""
                        }
                      >
                        <div className="col-md-3" style={{ padding: 0 }}>
                          <div className="input-group">
                            <div className="input-group-prepend">
                              <div className="input-group-text">$</div>
                            </div>
                            <Field
                              as={CInput}
                              name="paymentAmountOther"
                              id="payment-amount-other"
                              className="form-control"
                            />
                          </div>
                        </div>
                        <div className="error-text">
                          {errors.paymentAmountOther &&
                          touched.paymentAmountOther
                            ? (errors.paymentAmountOther as string)
                            : null}
                        </div>
                      </CFormGroup>
                    )}
                  </CFormGroup>
                  <CFormGroup className={errors.paymentMethodId ? "error" : ""}>
                    <CLabel>Payment account</CLabel>
                    {paymentMethodRadios}
                    {paymentMethods?.data?.length === 0 && (
                      <p>No bank accounts found</p>
                    )}
                    <div className="error-text">{errors.paymentMethodId}</div>
                    <CButton
                      onClick={() => toggleBankAccountModal(true, null)}
                      style={{ marginTop: 10 }}
                    >
                      Add Bank Account
                    </CButton>
                    <CButton
                      onClick={() => toggleCreditCardModal(true, null)}
                      style={{ marginTop: 10, marginLeft: 10 }}
                    >
                      Add Credit Card
                    </CButton>
                  </CFormGroup>
                  <div>
                    {lastSuccessfulPayment ? (
                      <p>
                        Last successful payment received on{" "}
                        <strong>
                          {formatDate(lastSuccessfulPayment.paymentDate)}
                        </strong>{" "}
                        for{" "}
                        <strong>
                          {formatCurrency(lastSuccessfulPayment.amount)}
                        </strong>
                        . <Link to="/payment-history">See payment history</Link>
                      </p>
                    ) : (
                      <p>
                        No prior payments have been received.{" "}
                        <Link to="/payment-history">See payment history</Link>
                      </p>
                    )}
                    {isAutopayEnabled ? (
                      <AutopayInfoAlert enrolled />
                    ) : (
                      <p>
                        <strong>AutoPay is Disabled.</strong> Enable from the{" "}
                        <Link to="/auto-pay">AutoPay</Link> page.
                      </p>
                    )}
                  </div>
                </CCardBody>
                <CCardFooter>
                  {!isValid && submitCount > 0 && (
                    <CAlert color="danger">
                      This form contains one or more errors. Please make
                      corrections and try again.
                    </CAlert>
                  )}
                  <CButton
                    type="submit"
                    color="primary"
                    disabled={isSubmitting || (isValidating && !isValid)}
                  >
                    Make Payment
                  </CButton>
                  <SecureDataTransfer style={{ paddingLeft: 16 }} />
                </CCardFooter>
              </Form>
              {bankAccountModal && (
                <AddBankAccountModal
                  setModal={() => toggleBankAccountModal(false, setFieldValue)}
                  modal={bankAccountModal}
                />
              )}
              {creditCardModal && (
                <AddCreditCardModal
                  setModal={() => toggleCreditCardModal(false, setFieldValue)}
                  modal={creditCardModal}
                />
              )}
            </>
          )}
        </Formik>
      </CCard>

      {paymentConfirmModal}
    </div>
  );
};

export default MakePayment;
